// Interpolates CPQ message templates like:
//   "<p><strong>Cell Open Area:</strong> {OPEN_AREA/OPEN_AREA}</p>"
//   "{APPLICATION:name}"
//
// Tokens:
//   {QCODE/ACODE}            → answers[QCODE/ACODE] (the stored value)
//   {QCODE/ACODE:name}       → stripped label_html of the matching answer
//   {QCODE/ACODE:alt_name}   → answer.name
//   {QCODE/ACODE:desc}       → answer.desc
//
// Unknown values render as empty string. Output stays HTML (caller renders
// via dangerouslySetInnerHTML).

import type { AnswerState, Question } from "../types";

const TOKEN_RE =
  /\{([A-Z0-9_]+)\/([A-Z0-9_]+)(?::([a-z_]+))?\}/g;

export function interpolateMessage(
  html: string,
  answers: AnswerState,
  questionsByCode: Map<string, Question>
): string {
  if (!html) return "";
  return html.replace(
    TOKEN_RE,
    (_match, qCode: string, aCode: string, mode: string | undefined) => {
      const key = `${qCode}/${aCode}`;
      const v = answers[key];

      if (!mode) {
        if (v === undefined || v === null) return "";
        // For boolean-true selections, look up the answer.code instead
        // of "true" (this matches CPQ semantics for {QCODE/ACODE}).
        if (v === true) return aCode;
        // Don't show "NaN" / "Infinity" — render as empty so the user sees
        // a blank cell instead of bad math leakage.
        if (typeof v === "number" && !Number.isFinite(v)) return "";
        return String(v);
      }

      const q = questionsByCode.get(qCode);
      const ans = q?._answers.find(
        (a) => a.custrecord_cpqc_answer_code === aCode
      );
      if (!ans) return "";

      switch (mode) {
        case "name": {
          const lbl = (ans.custrecord_cpqc_answer_label_html as string) ?? "";
          return lbl.replace(/<[^>]+>/g, "").trim();
        }
        case "alt_name":
          return (ans.name as string) ?? "";
        case "desc":
          return (ans.custrecord_cpqc_answer_desc as string) ?? "";
        default:
          return "";
      }
    }
  );
}
